import { useState } from "react"
import { Link } from "react-router-dom"
import { DriverTypeAhead } from "./TypeAheadComponents"
import { Query } from '@apollo/client/react/components'
import Select from "react-select"
import gql from 'graphql-tag'

const AllCarsQuery = gql`query AllCars($searchTerm: String){
    dataList: allCars(searchTerm: $searchTerm, first: 10){
        edges{
            node{
                id pk vin customName make model year
            }
        }
    }
    
}`

const AllIncidentsQuery = gql`query AllIncidents($searchTerm: String, $incidentId: ID){
    dataList: allIncidents(searchTerm: $searchTerm, id: $incidentId, first: 10, orderBy:["-id"]){
        edges{
            node{
                id 
                pk
                title
            }
        }
    }
}`

const AllLiabilityClaimsQuery = gql`query AllLiabilityClaims($searchTerm: String){
    dataList: allLiabilityClaims(searchTerm: $searchTerm, first: 10, orderBy:["-date_added"]){
        edges{
            node{
                id
                pk
                externalId
                incident {
                    id
                    title
                }
            }
        }
    }
}`

const AllSubrogationClaimsQuery = gql`query AllSubrogationClaims($searchTerm: String){
    dataList: allSubrogationClaims(searchTerm: $searchTerm, first: 10, orderBy:["-date_added"]){
        edges{
            node{
                id
                pk
                externalId
                incident {
                    id
                    title
                }
            }
        }
    }
}`


const AllCarIssuesQuery = gql`query AllCarIssues($searchTerm: String){
    dataList: allCarIssues(searchTerm: $searchTerm, first: 10, orderBy:["-id"]){
        edges{
            node{
                id
                pk
                issueCategory { id category }
                car{ id pk year make model }
            }
        }
    }
}`

export const CUSTOM_FK_CONFIG = {
    "driver": {
        getLink: (node) => `/driver/${node?.grapheneId}`,
        getLabel: (node) => node?.valueString || `Driver #${node?.value}` || "View Driver",
        inputComponent: (props) => <DriverTypeAhead {...props} />
    },
    "car": {
        getLink: (node) => `/car/${node?.value}`,
        getLabel: (node) => node?.valueString || `Car #${node?.value}` || "View Car",
        inputComponent: (props) => <TypeAheadInput query={AllCarsQuery} renderLabel={(node) => `#${node.pk} · ${node.year} ${node.make} ${node.model} · ${node.vin}`} placeholder="Select a car" {...props} />
    },
    "incident": {
        getLink: (node) => `/incident/${node?.grapheneId}`,
        getLabel: (node) => node?.valueString || `Incident #${node?.value}` || "View Incident",
        inputComponent: (props) => <TypeAheadInput query={AllIncidentsQuery} renderLabel={(node) => `#${node.pk} · ${node.title}`} placeholder="Select an incident" {...props} />
    },
    "carissues": {
        getLink: (node) => `/car-issues/${node?.grapheneId}`,
        getLabel: (node) => node?.valueString || `Car Issue #${node?.value}` || "View Car Issue",
        inputComponent: (props) => <TypeAheadInput query={AllCarIssuesQuery} renderLabel={(node) => `#${node.pk} · ${node.issueCategory?.category || "No Category Selected"} (${node.car?.year} ${node.car?.make} ${node.car?.model} #${node.car?.pk})`} placeholder="Select a car issue" {...props} />
    },
    "liabilityclaim": {
        getLink: (node) => `/liability-claims/${node?.grapheneId}`,
        getLabel: (node) => node?.valueString || `Liability Claim #${node?.value}` || "View Liability Claim",
        inputComponent: (props) => <TypeAheadInput query={AllLiabilityClaimsQuery} renderLabel={(node) => `#${node.pk} · ${node.externalId || "No Claim #"} · ${node.incident?.title || "No Reference"}`} placeholder="Select a liability claim" {...props} />
    },
    "subrogationclaim": {
        getLink: (node) => `/subrogation-claims/${node?.grapheneId}`,
        getLabel: (node) => node?.valueString || `Subrogation Claim #${node?.value}` || "View Subrogation Claim",
        inputComponent: (props) => <TypeAheadInput query={AllSubrogationClaimsQuery} renderLabel={(node) => `#${node.pk} · ${node.externalId || "No Claim #"} · ${node.incident?.title || "No Reference"}`} placeholder="Select a subrogation claim" {...props} />
    },
}

export const renderCustomColumn = (columnValueNode) => {
    if (!columnValueNode) {
        return <span className={"text-muted"}>--</span>
    }

    if (columnValueNode?.column?.columnType === "FK_OBJECT") {
        let model = columnValueNode?.column?.contentType?.model || ""
        let config = CUSTOM_FK_CONFIG[model]
        if (config) {
            return (
                <Link to={config.getLink(columnValueNode)} target="_blank">
                    {config.getLabel(columnValueNode)}
                </Link>
            )
        } else {
            return <span className={columnValueNode?.valueString ? "" : "text-muted"}>{columnValueNode?.valueString || "--"}</span>
        }
    } else if (columnValueNode?.column?.columnType === "URL" && columnValueNode?.valueString) {
        const url = columnValueNode.valueString
        const displayText = url.length > 35 ? url.slice(0, 35) + "…" : url
        return (
            <a href={url} target="_blank" rel="noopener noreferrer" title={url}>
                {displayText}
            </a>
        )
    }

    return <span className={columnValueNode?.valueString ? "" : "text-muted"}>{columnValueNode?.valueString || "--"}</span>
}

export const ForeignKeyInput = ({ model, onChange, defaultValue }) => {
    const config = CUSTOM_FK_CONFIG[model]
    if (config) {
        const InputComponent = config.inputComponent
        return <InputComponent
            // defaultValue={defaultValue} // Commenting out defaultValue for now as it is not working properly with the way the input components are currently implemented. Will need to refactor the input components to properly support default values.
            onChange={onChange}
        />
    }
}

export const TypeAheadInput = ({ query, onChange, renderLabel = null, placeholder = "Select an option" }) => {
    const [searchTerm, setSearchTerm] = useState("")

    return (
        <Query query={query} variables={{ searchTerm }}>
            {({ loading, error, data }) => {
                if (loading) return <Select isLoading={true}></Select>
                if (error) return <Select isLoading={false} isDisabled={true} placeholder={`Error! ${error.message}`}></Select>

                let options = data?.dataList?.edges?.length > 0 ? data.dataList.edges.map(object => ({
                    value: object.node.id,
                    label: renderLabel ? renderLabel(object.node) : object.node.id
                })) : []

                return <Select
                    options={options}
                    placeholder={placeholder}
                    onInputChange={(inputValue) => { setSearchTerm(inputValue) }}
                    onChange={(selected) => onChange(selected.value)}
                />
            }}
        </Query>
    )
}